import {
  BLACK_LIST_STRING_VALUE_MESSAGE,
  ERROR_BOUNDARY_MESSAGE,
  ErrorCode,
  FAILED_TO_FIND_CONFIG_FOR_ITEM_MESSAGE,
  FAILED_TO_FIND_CONTENT_CONFIG_FOR_STATISTIC_MESSAGE,
  FAILED_TO_FIND_VALIDATION_RULES_FOR_STATISTIC_MESSAGE,
  FAILED_TO_LOAD_ASSETS_MESSAGE,
  FAILED_TO_LOAD_FONTS_MESSAGE,
  FAILED_TO_PARSE_DATE_VALUE_MESSAGE,
  FAILED_TO_PARSE_NUMBER_VALUE_MESSAGE,
  FAILED_TO_PARSE_PLURAL_NUMBER_VALUE_MESSAGE,
  INVALID_STRING_VALUE_MESSAGE,
  LESS_THAN_NUMBER_VALIDATION_ERROR_MESSAGE,
  LONG_STRING_VALUE_MESSAGE,
  LOWERCASE_LETTERS_IN_STRING_VALUE_MESSAGE,
  MANY_SIGNS_AFTER_COMMA_IN_NUMBER_VALIDATION_ERROR_MESSAGE,
  MORE_THAN_NUMBER_VALIDATION_ERROR_MESSAGE,
  NEGATIVE_NUMBER_VALIDATION_ERROR_MESSAGE,
  NOT_INTEGER_NUMBER_VALIDATION_ERROR_MESSAGE,
  SHORT_STRING_VALUE_MESSAGE,
  WHITE_LIST_NUMBER_VALUE_MESSAGE,
} from '@/services/ErrorService/ErrorService.constants';

import {uuid4} from '@specials/project-user';

interface FrontendErrorData {
  errorCode: string;
  message: string;
  requestId: string;
  timestamp: string;
  error: Error;
}

const FRONTEND_ERROR_NAME = 'FrontendError';

export class FrontendError extends Error {
  public readonly errorCode: string;
  public readonly message: string;
  public readonly requestId: string;
  public readonly timestamp: string;
  public readonly error: Error;
  public readonly isFrontendError: true;
  public readonly name: string;

  constructor(errorData: FrontendErrorData) {
    super(errorData.message);
    this.errorCode = errorData.errorCode;
    this.message = errorData.message;
    this.requestId = errorData.requestId;
    this.timestamp = errorData.timestamp;
    this.error = errorData.error;
    this.isFrontendError = true;
    this.name = FRONTEND_ERROR_NAME;
  }

  public static isFrontendError = (error: unknown): error is FrontendError => {
    if (!error) {
      return false;
    }

    return typeof error === 'object' && 'isFrontendError' in error;
  };

  public static createBoundaryError = (error: Error): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.BoundaryError,
      message: ERROR_BOUNDARY_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error,
    });
  };

  public static createFontLoaderError = (error: Error): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.LoadFontsError,
      message: FAILED_TO_LOAD_FONTS_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error,
    });
  };

  public static createAssetsLoaderError = (error: Error): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.LoadAssetsError,
      message: FAILED_TO_LOAD_ASSETS_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error,
    });
  };

  public static createCacheServiceDataParseError = (error: Error, key: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.CacheError,
      message: `Failed to parse data from cache with key: ${key} and value: ${value}`,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error,
    });
  };

  public static createShareError = (error: Error, message: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.ShareError,
      message,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error,
    });
  };

  public static createFindItemConfigError = (itemLabel: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: FAILED_TO_FIND_CONFIG_FOR_ITEM_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Config for label ${itemLabel} is not found`),
    });
  };

  public static createFindStatisticContentConfigError = (metric_desc: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: FAILED_TO_FIND_CONTENT_CONFIG_FOR_STATISTIC_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Content config for ${metric_desc} is not found`),
    });
  };

  public static createFindStatisticValidationRuleError = (metric_desc: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: FAILED_TO_FIND_VALIDATION_RULES_FOR_STATISTIC_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`No validation rules for ${metric_desc} with value ${value}`),
    });
  };

  public static createParsePluralNumberValueError = (metric_desc: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: FAILED_TO_PARSE_PLURAL_NUMBER_VALUE_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Failed tp parse plural number for ${metric_desc} with value ${value}`),
    });
  };

  public static createInvalidStringValueError = (metric_desc: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: INVALID_STRING_VALUE_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Parameter ${metric_desc} has invalid string value ${value}`),
    });
  };

  public static createLongStringValueError = (metric_desc: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: LONG_STRING_VALUE_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Parameter ${metric_desc} has too long string value ${value}`),
    });
  };

  public static createShortStringValueError = (metric_desc: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: SHORT_STRING_VALUE_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Parameter ${metric_desc} has too short string value ${value}`),
    });
  };

  public static createUpperCaseStringValueError = (metric_desc: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: LOWERCASE_LETTERS_IN_STRING_VALUE_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Parameter ${metric_desc} has string value ${value} with letters in lower case`),
    });
  };

  public static createBlackListStringValueError = (metric_desc: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: BLACK_LIST_STRING_VALUE_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Parameter ${metric_desc} has string value ${value} which is in blacklist`),
    });
  };

  public static createWhiteListNumberValueError = (metric_desc: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: WHITE_LIST_NUMBER_VALUE_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Parameter ${metric_desc} has number value ${value} which is not in whitelist`),
    });
  };

  public static createParseNumberValueError = (metric_desc: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: FAILED_TO_PARSE_NUMBER_VALUE_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Parameter ${metric_desc} with value ${value} is not an number`),
    });
  };

  public static createNegativeNumberValueError = (metric_desc: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: NEGATIVE_NUMBER_VALIDATION_ERROR_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Parameter ${metric_desc} with value ${value} is negative`),
    });
  };

  public static createLessThanValueError = (metric_desc: string, value: string, ruleValue: number): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: LESS_THAN_NUMBER_VALIDATION_ERROR_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Parameter ${metric_desc} with value ${value} is less than ${ruleValue}`),
    });
  };

  public static createMoreThanValueError = (metric_desc: string, value: string, ruleValue: number): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: MORE_THAN_NUMBER_VALIDATION_ERROR_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Parameter ${metric_desc} with value ${value} is more than ${ruleValue}`),
    });
  };

  public static createNotIntegerNumberValueError = (metric_desc: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: NOT_INTEGER_NUMBER_VALIDATION_ERROR_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Parameter ${metric_desc} with value ${value} is not integer`),
    });
  };

  public static createNotDateValueError = (metric_desc: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: FAILED_TO_PARSE_DATE_VALUE_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Parameter ${metric_desc} with value ${value} is not a valid ISOString date`),
    });
  };

  public static createManySignsAfterCommaNumberValueError = (metric_desc: string, value: string): FrontendError => {
    return new FrontendError({
      errorCode: ErrorCode.RetrieveStatisticError,
      message: MANY_SIGNS_AFTER_COMMA_IN_NUMBER_VALIDATION_ERROR_MESSAGE,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
      error: new Error(`Parameter ${metric_desc} with value ${value} has many signs after comma`),
    });
  };
}
